import { useEffect, useState } from 'preact/hooks';
import { AdminShell } from './AdminShell';
import { api } from '../../lib/api';
import { Button } from '../../components/Button';
import {
  Table, Th, Td, Pager, Toolbar, Badge, Modal, fmtDate, fieldStyle, type Page, PAGE_SIZE,
} from './ui';
import { GIFT_DURATIONS, useGift } from './gift';

type AdminUser = {
  id: string;
  email: string;
  emailVerified: boolean;
  role: string;
  status: string;
  locale: string;
  createdAt: string;
  lastLoginAt: string | null;
};

type UsersResponse = Page & { users: AdminUser[] };

export function AdminUsers() {
  return <AdminShell tab="users">{() => <UsersTable />}</AdminShell>;
}

function UsersTable() {
  const [rows, setRows] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [giftFor, setGiftFor] = useState<AdminUser | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (search) params.set('search', search);
    if (role) params.set('role', role);
    api<UsersResponse>(`/api/admin/users?${params}`)
      .then((r) => { if (!cancelled) { setRows(r.users); setTotal(r.total); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [offset, search, role]);

  return (
    <>
      <Toolbar
        search={search}
        onSearch={(v) => { setOffset(0); setSearch(v); }}
        placeholder="Search by email…"
        filters={[
          {
            value: role,
            onChange: (v) => { setOffset(0); setRole(v); },
            options: [
              { value: '', label: 'All roles' },
              { value: 'user', label: 'Users' },
              { value: 'admin', label: 'Admins' },
            ],
          },
        ]}
      />

      {error && <p style={{ color: 'var(--wine)' }}>{error}</p>}

      <Table>
        <thead>
          <tr>
            <Th>Email</Th>
            <Th>Role</Th>
            <Th>Status</Th>
            <Th>Created</Th>
            <Th>Last login</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.id}>
              <Td>
                <span style={{ fontWeight: 600 }}>{u.email}</span>
                {!u.emailVerified && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--ink-3)' }}>unverified</span>
                )}
              </Td>
              <Td>
                <Badge tone={u.role === 'admin' ? 'ember' : 'neutral'}>{u.role}</Badge>
              </Td>
              <Td>
                <Badge tone={u.status === 'active' ? 'green' : u.status === 'deleted' ? 'wine' : 'neutral'}>
                  {u.status}
                </Badge>
              </Td>
              <Td muted>{fmtDate(u.createdAt)}</Td>
              <Td muted>{fmtDate(u.lastLoginAt)}</Td>
              <Td>
                <GiftIconButton
                  disabled={u.status === 'deleted'}
                  onClick={() => setGiftFor(u)}
                />
              </Td>
            </tr>
          ))}
          {!loading && rows.length === 0 && (
            <tr><Td colSpan={6} muted>No users found.</Td></tr>
          )}
        </tbody>
      </Table>

      <Pager total={total} offset={offset} count={rows.length} loading={loading} onOffset={setOffset} />

      {giftFor && <GiftModal user={giftFor} onClose={() => setGiftFor(null)} />}
    </>
  );
}

// Icon-only action that opens the gift modal for a row. Uses a gift glyph so
// the intent reads at a glance without widening the table.
function GiftIconButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={disabled ? 'Account deleted — cannot gift' : 'Gift a subscription'}
      aria-label="Gift a subscription"
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 32, height: 32, borderRadius: 9, border: 'none',
        background: 'var(--surface-2)', color: 'var(--ember)', fontSize: 15,
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1,
      }}
    >
      🎁
    </button>
  );
}

// Gift modal opened from a Users row. The recipient is fixed to that row's
// account, so there's no email field — just pick a duration and grant, or
// revoke an earlier gift. Shares useGift with the Subscriptions tab card.
function GiftModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const [duration, setDuration] = useState('month');
  const { busy, msg, gift, revoke } = useGift();

  return (
    <Modal title="Gift a subscription" onClose={onClose}>
      <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 14, lineHeight: 1.5 }}>
        Grant the <code>pro</code> entitlement to{' '}
        <strong style={{ color: 'var(--ink)' }}>{user.email}</strong> as a RevenueCat
        promotional — no store billing. The recipient needs a shotmeal account
        (signed in to the app at least once).
      </div>

      <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--ink-3)', marginBottom: 6 }}>
        Duration
      </label>
      <select
        value={duration}
        onChange={(e) => setDuration((e.target as HTMLSelectElement).value)}
        style={{ ...fieldStyle, cursor: 'pointer', width: '100%', marginBottom: 18 }}
        disabled={busy}
      >
        {GIFT_DURATIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
      </select>

      <div style={{ display: 'flex', gap: 10 }}>
        <Button
          variant="ember"
          size="sm"
          disabled={busy}
          onClick={() => gift(user.email, duration)}
        >
          Gift {GIFT_DURATIONS.find((d) => d.value === duration)?.label}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() => revoke(user.email)}
        >
          Revoke gift
        </Button>
      </div>

      {msg && (
        <div style={{ fontSize: 13, marginTop: 14, color: msg.ok ? '#1c7a45' : 'var(--wine)' }}>
          {msg.text}
        </div>
      )}
    </Modal>
  );
}
